// src/components/Rates/CalendarView.js

import React, { useState, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

const CalendarView = ({ selectedDate, onDateSelect, rateStatus = {} }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);

  // Utility function to format date in local time
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    generateCalendar();
  }, [currentMonth]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    while (current <= lastDay || current.getDay() !== 0) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    setCalendarDays(days);
  };

  const getRateStatusColor = (date) => {
    const dateStr = formatDateLocal(date); // Use local formatting
    const status = rateStatus[dateStr];

    if (!status) return "";
    if (status === "set" || status === "PUBLISHED")
      return "bg-green-100 text-green-800";
    if (status === "copied" || status === "COPIED")
      return "bg-yellow-100 text-yellow-800";
    if (status === "pending" || status === "DRAFT")
      return "bg-orange-100 text-orange-800";
    if (status === "scheduled" || status === "SCHEDULED")
      return "bg-blue-100 text-blue-800";
    return "";
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return formatDateLocal(date) === selectedDate; // Use local formatting
  };

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleDateClick = (date) => {
    // Format the date in local time and pass it
    const formattedDate = formatDateLocal(date);
    onDateSelect(formattedDate);
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => handleMonthChange(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        <h3 className="text-lg font-semibold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button
          onClick={() => handleMonthChange(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next month"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-gray-600 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, index) => {
          const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
          const statusColor = getRateStatusColor(date);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)} // Use the new handler
              disabled={!isCurrentMonth}
              className={`
                relative p-2 text-sm rounded-lg transition-all
                ${!isCurrentMonth ? "text-gray-400" : "text-gray-900"}
                ${isToday(date) ? "ring-2 ring-blue-500" : ""}
                ${isSelected(date) ? "bg-blue-600 text-white" : ""}
                ${statusColor && !isSelected(date) ? statusColor : ""}
                ${
                  isCurrentMonth && !isSelected(date) ? "hover:bg-gray-100" : ""
                }
                disabled:cursor-not-allowed
              `}
            >
              <span className="block">{date.getDate()}</span>

              {statusColor && (
                <span className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-current rounded-full"></span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs">
        <div className="flex items-center">
          <span className="w-3 h-3 bg-green-100 rounded mr-1"></span>
          <span>Published</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 bg-yellow-100 rounded mr-1"></span>
          <span>Copied</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 bg-orange-100 rounded mr-1"></span>
          <span>Draft</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 bg-blue-100 rounded mr-1"></span>
          <span>Scheduled</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
